import { FC, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { Carousel } from 'dibs-carousel';
import { Link } from 'dibs-elements/exports/Link';
import { SbSharedInteriorsHeader } from '../SbSharedInteriorsModule/SbSharedInteriorsHeader';
import { SbRespInteriorsViewButton } from './SbRespInteriorsViewButton';
import { SbRespInteriorsLazyLoadImage } from './SbRespInteriorsLazyLoadImage';

import styles from './SbRespInteriorsPhotoCarousel.scss';

import { SbRespInteriorsPhotoCarousel_photos } from './__generated__/SbRespInteriorsPhotoCarousel_photos.graphql';
import { SbRespInteriorsPhotoCarousel_itemSearch } from './__generated__/SbRespInteriorsPhotoCarousel_itemSearch.graphql';

const ITEMS_PER_PAGE = 4;

type ImageLinkProps = {
    index: number;
    onClick: () => void;
};

const ImageLink: FC<ImageLinkProps> = ({ children, index, onClick }) => (
    <Link
        className={styles.imgLink}
        buyerLinkType="standardLinkNoEffect"
        underline="none"
        dataTn={`interior-photo-${index}`}
        onClick={onClick}
    >
        {children}
    </Link>
);

type Props = {
    photos: SbRespInteriorsPhotoCarousel_photos;
    itemSearch: SbRespInteriorsPhotoCarousel_itemSearch;
    onPhotoIndexChange: (newIndex: number) => void;
    onViewInteriorClick: () => void;
    onPhotoClick: () => void;
};

const SbRespInteriorsPhotoCarouselComponent: FC<Props> = ({
    photos,
    itemSearch,
    onPhotoIndexChange,
    onViewInteriorClick,
    onPhotoClick,
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const photoCount = photos.length;
    return (
        <div className={styles.wrapper}>
            <div className={styles.headerWrapper}>
                <SbSharedInteriorsHeader itemSearch={itemSearch} />
            </div>
            <div className={styles.imgWrapper}>
                {photoCount < ITEMS_PER_PAGE ? (
                    <div className={styles.centeredImgWrapper}>
                        {photos.map((photo, index) => (
                            <ImageLink
                                key={photo.path || index}
                                index={index}
                                onClick={onPhotoClick}
                            >
                                <SbRespInteriorsLazyLoadImage
                                    imgClass={styles.img}
                                    lazyLoadClass={styles.lazyLoad}
                                    photo={photo}
                                />
                            </ImageLink>
                        ))}
                    </div>
                ) : (
                    <Carousel
                        classNames={{
                            listWrapper: imageLoaded ? styles.carouselGradient : undefined,
                            item: styles.carouselItem,
                        }}
                        dataTn="sb-interiors-photo-carousel"
                        totalItems={photos.length}
                        isInfinite={false}
                        slideCarouselItems
                        itemsPerPage={ITEMS_PER_PAGE}
                        step={ITEMS_PER_PAGE}
                        onIndexChange={({ index }: { index: number }) => onPhotoIndexChange(index)}
                        renderItem={({
                            index,
                            isCurrentItem,
                        }: {
                            index: number;
                            isCurrentItem: boolean;
                        }) => {
                            const currentPhoto = photos[index];
                            return (
                                <>
                                    {isCurrentItem && imageLoaded && (
                                        <div className={styles.overlay}>
                                            <SbRespInteriorsViewButton
                                                onClick={onViewInteriorClick}
                                            />
                                        </div>
                                    )}
                                    <ImageLink index={index} onClick={onPhotoClick}>
                                        <SbRespInteriorsLazyLoadImage
                                            imgClass={styles.carouselImg}
                                            lazyLoadClass={styles.carouselLazyLoad}
                                            photo={currentPhoto}
                                            onImageLoaded={() => setImageLoaded(true)}
                                        />
                                    </ImageLink>
                                </>
                            );
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export const SbRespInteriorsPhotoCarousel = createFragmentContainer(
    SbRespInteriorsPhotoCarouselComponent,
    {
        itemSearch: graphql`
            fragment SbRespInteriorsPhotoCarousel_itemSearch on ItemSearchQueryConnection {
                ...SbSharedInteriorsHeader_itemSearch
            }
        `,
        photos: graphql`
            fragment SbRespInteriorsPhotoCarousel_photos on PhotosSearchBrowsePhoto
            @relay(plural: true) {
                name
                path
            }
        `,
    }
);
